// Toast — substituto do alert(). Uso:
//
//   // em src/index.tsx (ou App.tsx), ao redor do <App />:
//   <ToastProvider><App /></ToastProvider>
//
//   // em qualquer componente:
//   const toast = useToast();
//   toast.success("Marca publicada");
//   toast.error("Falha ao salvar", "verifique a rede");

import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from "react";
import styles from "./Toast.module.scss";

type Kind = "success" | "error" | "info" | "warn";
interface ToastItem {
  id: number;
  kind: Kind;
  title: string;
  detail?: string;
  ttl: number;
}

interface Api {
  push: (kind: Kind, title: string, detail?: string, ttl?: number) => number;
  success: (title: string, detail?: string) => number;
  error: (title: string, detail?: string) => number;
  info: (title: string, detail?: string) => number;
  warn: (title: string, detail?: string) => number;
  dismiss: (id: number) => void;
}

const Ctx = createContext<Api | null>(null);

export function useToast(): Api {
  const api = useContext(Ctx);
  if (!api) throw new Error("useToast fora do ToastProvider");
  return api;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setItems((cur) => cur.filter((i) => i.id !== id));
  }, []);

  const push = useCallback(
    (kind: Kind, title: string, detail?: string, ttl = 5000) => {
      const id = nextId.current++;
      setItems((cur) => [...cur, { id, kind, title, detail, ttl }]);
      return id;
    },
    [],
  );

  const api: Api = {
    push,
    success: (t, d) => push("success", t, d),
    error: (t, d) => push("error", t, d, 8000),
    info: (t, d) => push("info", t, d),
    warn: (t, d) => push("warn", t, d),
    dismiss,
  };

  return (
    <Ctx.Provider value={api}>
      {children}
      <div className={styles.region} role="region" aria-live="polite" aria-label="Notificações">
        {items.map((i) => (
          <ToastItemView key={i.id} item={i} dismiss={dismiss} />
        ))}
      </div>
    </Ctx.Provider>
  );
}

function ToastItemView({ item, dismiss }: { item: ToastItem; dismiss: (id: number) => void }) {
  useEffect(() => {
    if (!item.ttl) return;
    const t = setTimeout(() => dismiss(item.id), item.ttl);
    return () => clearTimeout(t);
  }, [item.id, item.ttl, dismiss]);

  return (
    <div className={`${styles.toast} ${styles[item.kind]}`} role="status">
      <div className={styles.body}>
        <div className={styles.title}>{item.title}</div>
        {item.detail && <div className={styles.detail}>{item.detail}</div>}
      </div>
      <button className={styles.close} onClick={() => dismiss(item.id)} aria-label="Fechar">
        ×
      </button>
    </div>
  );
}
